import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import clsx from "clsx";
import { Page } from "../../../components/Page";
import { TooltipLayer } from "./components/TooltipLayer";
import { KpiTile } from "./components/KpiTile";
import { LoadError } from "./components/LoadError";
import {
  FleetTable,
  LockActionRequest,
  operatorBlockedOnNote,
  sectionAnchorId,
  WindowMode,
} from "./components/FleetTable";
import { OnboardingSection } from "./components/OnboardingSection";
import { ActionModal } from "./components/modals/ActionModal";
import { LockActionModal } from "./components/modals/LockActionModal";
import { NoteModal } from "./components/modals/NoteModal";
import { SettingsModal } from "./components/modals/SettingsModal";
import { UserDetailPanel } from "../../onboarding-tracking/components/UserDetailPanel";
import {
  useApplyLockFor,
  useEngines,
  useFleetMetrics,
  useFleetSummary,
  useHotelList,
  useLogNote,
  useRemoveLockFor,
  useTrackingSettings,
  useUpdateTrackingSettings,
} from "./hooks/useTrackingHealth";
import { useOnboardingPipelines } from "./hooks/useOnboardingPipelines";
import { useHiddenOnboardingRows } from "./hidden-rows";
import { OnboardingMergedRow, splitFleet } from "./onboarding-merge";
import { fmtDate, fmtMoney } from "./format";
import type { FleetMetricsRow, HotelListRow } from "./types";

/**
 * The one dashboard (census.html's structure as a live surface): KPI tiles
 * that SCROLL to their section, then every hotel flattened into state order —
 * Still onboarding → Unhealthy → Monitoring → Healthy → Low volume → Paused.
 * No worklist tab; the incident chips ride the rows and the
 * engine-investigation rollups live on the Engines page.
 */

const PAGE_LIMIT = 200;

/**
 * The merged fleet table fetches the WHOLE fleet in one read and filters
 * client-side: the backend clamps `limit` at 200 against a fleet of ~110,
 * and the onboarding join must never be computed against a server-filtered
 * subset (an engine-filtered response would misread joined pipelines as
 * "not in Ads fleet yet").
 */
const FLEET_FETCH = { page: 1, limit: PAGE_LIMIT } as const;

const SELECT_CLASS =
  "h-10 px-3 rounded border border-neutral-400 text-sm bg-white";

interface FleetFilters {
  engine: string;
  q: string;
}

function matchesLiveRow(row: HotelListRow, f: FleetFilters): boolean {
  if (f.engine && !row.engines.some((e) => e.key === f.engine)) return false;
  if (f.q) {
    const term = f.q.toLowerCase();
    if (
      !row.hotelName.toLowerCase().includes(term) &&
      !(row.ownerEmail ?? "").toLowerCase().includes(term) &&
      !(row.websiteUrl ?? "").toLowerCase().includes(term) &&
      !row.customerId.includes(term.replace(/\D/g, "") || "\0")
    )
      return false;
  }
  return true;
}

function matchesOnboardingRow(
  row: OnboardingMergedRow,
  f: FleetFilters,
): boolean {
  if (f.engine) {
    const viaHotel = row.hotel?.engines.some((e) => e.key === f.engine);
    const viaPms = row.user?.pmsType?.toLowerCase() === f.engine.toLowerCase();
    if (!viaHotel && !viaPms) return false;
  }
  if (f.q) {
    const term = f.q.toLowerCase();
    const name = row.user?.hotelName ?? row.hotel?.hotelName ?? "";
    const email = row.user?.userEmail ?? row.hotel?.ownerEmail ?? "";
    if (
      !name.toLowerCase().includes(term) &&
      !email.toLowerCase().includes(term)
    )
      return false;
  }
  return true;
}

/**
 * The wording the row is already showing, handed to the note modal so it opens
 * on the note that stands rather than on a blank: amending what is there is
 * the common act, and retyping it from memory is how a note loses its
 * history. A machine-stamped note is not an operator's writing (see
 * `operatorBlockedOnNote`), so it is no more editable here than it is
 * readable on the row — both surfaces read that hotel as noteless.
 */
function standingNoteOf(row: HotelListRow): string | null {
  return row.openIncident ? operatorBlockedOnNote(row.openIncident) : null;
}

export function FleetOverview() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const engine = searchParams.get("engine") ?? "";
  const q = searchParams.get("q") ?? "";
  const mode: WindowMode = searchParams.get("w") === "full" ? "full" : "recent";

  const setParam = useCallback(
    (patch: Record<string, string>) => {
      const next = new URLSearchParams(searchParams);
      for (const [key, value] of Object.entries(patch)) {
        if (value) next.set(key, value);
        else next.delete(key);
      }
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  // Debounced search — filters serialize to query params so views share.
  const [searchText, setSearchText] = useState(q);
  useEffect(() => setSearchText(q), [q]);
  useEffect(() => {
    const handle = setTimeout(() => {
      if (searchText !== q) setParam({ q: searchText });
    }, 300);
    return () => clearTimeout(handle);
  }, [searchText, q, setParam]);

  const summary = useFleetSummary();
  const engines = useEngines();
  const hotels = useHotelList(FLEET_FETCH);
  const metrics = useFleetMetrics();
  const pipelines = useOnboardingPipelines();
  const hiddenRows = useHiddenOnboardingRows();
  const { isHidden: isRowHidden } = hiddenRows;

  // The pipeline drawer stores only the id — the live object is re-looked-up
  // from list data each render so a refetch refreshes an open drawer.
  const [drawerUserId, setDrawerUserId] = useState<string | null>(null);
  const [lockModal, setLockModal] = useState<LockActionRequest | null>(null);
  const [noteRow, setNoteRow] = useState<HotelListRow | null>(null);
  const logNote = useLogNote(
    noteRow?.customerId ?? "",
    noteRow?.openIncident?.id ?? null,
  );
  const applyLock = useApplyLockFor();
  const removeLock = useRemoveLockFor();
  // The fleet-wide settings (backend ADR 0039): read when the modal opens, so
  // its fields seed from the row as it stands now, not from a page-load copy.
  const [settingsOpen, setSettingsOpen] = useState(false);
  const settings = useTrackingSettings(settingsOpen);
  const updateSettings = useUpdateTrackingSettings();

  const fleetFilters: FleetFilters = useMemo(
    () => ({ engine, q }),
    [engine, q],
  );

  const { live, onboarding } = useMemo(
    () => splitFleet(hotels.data?.items ?? [], pipelines.data ?? []),
    [hotels.data, pipelines.data],
  );
  const liveRows = useMemo(
    () => live.filter((row) => matchesLiveRow(row, fleetFilters)),
    [live, fleetFilters],
  );
  const onboardingRows = useMemo(
    () => onboarding.filter((row) => matchesOnboardingRow(row, fleetFilters)),
    [onboarding, fleetFilters],
  );

  const metricsByCid = useMemo(() => {
    if (!metrics.data) return null;
    return new Map<string, FleetMetricsRow>(
      metrics.data.rows.map((row) => [row.customerId, row]),
    );
  }, [metrics.data]);

  /**
   * Hiding an onboarding row is how an operator says "stuck indefinitely /
   * churned mid-onboarding" (see hidden-rows.ts — durable and fleet-wide since
   * 2026-08-17, so this count is the same for everyone), so the tile counts
   * what is left: a census that still counted them would overstate the live
   * pipeline. They stay accounted for in the tile's subtext rather than
   * disappearing.
   */
  const hiddenOnboardingCount = useMemo(
    () => onboarding.filter(isRowHidden).length,
    [onboarding, isRowHidden],
  );

  /** Census KPI: open Unhealthy 90-day spend, straight off the metric rows. */
  const unhealthyAtRisk = useMemo(() => {
    if (!metricsByCid) return null;
    let sum = 0;
    for (const row of live) {
      if (row.status.reported !== "unhealthy") continue;
      sum += metricsByCid.get(row.customerId)?.full.cost ?? 0;
    }
    return sum;
  }, [live, metricsByCid]);

  const drawerRow = drawerUserId
    ? onboarding.find((row) => row.user?.userId === drawerUserId) ?? null
    : null;

  const tiles = summary.data?.tiles;
  const fleetSize = summary.data?.fleetSize ?? 0;

  // KPI tiles scroll to their section — the page IS the flattened fleet.
  const scrollToSection = (key: string) => {
    document
      .getElementById(sectionAnchorId(key))
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const engineOptions = [
    { value: "", label: "All engines" },
    ...(engines.data?.registry ?? []).map((row) => ({
      value: row.engineKey,
      label: row.displayName,
    })),
  ];

  return (
    <Page>
      {/* No TOP padding on the scroll container, deliberately: Chrome pins a
          sticky cell to the container's CONTENT box, so a `pt-4` here would
          leave a 16px strip above the pinned table header with page content
          scrolling visibly through it. The breathing room moves inside, onto
          the first child, where it scrolls away like any other content. */}
      <div className="px-4 pb-4 md:h-[calc(100dvh-62px)] overflow-auto lg:h-[100dvh] bg-neutral-200 flex-1">
        <div className="flex items-center justify-between pt-4 mb-4 gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl font-semibold text-black">Tracking Health</h1>
            <div className="text-sm text-content-secondary mt-1">
              Fleet: {fleetSize} Google Ads accounts · MCC-defined · data
              through {fmtDate(summary.data?.dataThrough.overall)}
              {summary.data?.dataThrough.ga4 && (
                <> · GA4 through {fmtDate(summary.data.dataThrough.ga4)}</>
              )}
            </div>
          </div>
          {/* The Silenced tile used to be the only way into the Advanced page.
              Removing it from the KPI row (2026-08-15) must not orphan the
              route, so the link moves here — where a navigation belongs
              rather than where a census number does. Settings sits beside it
              (2026-09-18, backend ADR 0039): the fleet-wide thresholds are a
              disposition too, only one that applies to every hotel at once,
              and they open in place rather than on a page because there are
              six numbers and a reason. */}
          <div className="flex items-center gap-4">
            <button
              onClick={() => setSettingsOpen(true)}
              data-tip="Fleet-wide thresholds: the low-volume allowance, and (Advanced) the click floor and the allowance's backstop — changes take effect on the next daily pass"
              className="text-sm font-medium text-brand-primary hover:underline whitespace-nowrap"
            >
              Settings
            </button>
            <button
              onClick={() => navigate("/tracking/health/queues")}
              data-tip="Silenced incidents, accepted conditions, standing overrides and locks — the dispositions a human put in place"
              className="text-sm font-medium text-brand-primary hover:underline whitespace-nowrap"
            >
              Advanced →
            </button>
          </div>
        </div>

        {/* §5.1's seed banner (backend issue 43): until the seeding pass
            COMPLETES, states, incidents and alerts are latched — and on the
            tiles alone, never-seeded / seeding / seed-crashed are identical
            to a healthy fleet nobody has broken. Neutral for not-run /
            running (expected states); the failed banner is the one this
            exists for — a crashed seed holds the latch closed indefinitely
            while the page shows a fleet in perfect order. */}
        {summary.data && summary.data.seed.status !== "completed" && (
          <div
            className={
              summary.data.seed.status === "failed"
                ? "mb-4 rounded-lg bg-danger-light text-danger-dark px-4 py-2.5 text-sm"
                : "mb-4 rounded-lg bg-[#F4F4F6] text-content-secondary px-4 py-2.5 text-sm"
            }
          >
            {summary.data.seed.status === "not-run" && (
              <>
                The rollout seeding pass has not run — states, incidents and
                alerts are latched until it does.
              </>
            )}
            {summary.data.seed.status === "running" && (
              <>
                The rollout seeding pass is running (started{" "}
                {fmtDate(summary.data.seed.at)}) — states, incidents and
                alerts stay latched until it completes. A pass that crashed
                without stamping failure also reads as running; if this
                lingers, check the seed run's logs.
              </>
            )}
            {summary.data.seed.status === "failed" && (
              <>
                The rollout seeding pass FAILED (
                {fmtDate(summary.data.seed.at)}) — the latch is closed, so
                nothing derives states, opens incidents or alerts until the
                seed is re-run (npm run tracking:seed — it converges after a
                crash).
              </>
            )}
          </div>
        )}

        {/* KPI tiles, in the page's own section order — clicking scrolls to
            the grouping (census: the tiles ARE the table of contents). Every
            tile is now a state with a section below it; the Silenced tile
            was removed 2026-08-15 because it was the one that was not, and
            it sent the reader to a different page. A silence stays visible
            where it is acted on: the 🔕 badge on the row, and the Queues
            page's own Silenced card. */}
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 mb-4">
          <KpiTile
            label="Still onboarding"
            value={
              pipelines.isLoading || hotels.isLoading
                ? "—"
                : onboarding.length - hiddenOnboardingCount
            }
            numberClassName="text-content-primary"
            hint={
              hiddenOnboardingCount > 0
                ? `+${hiddenOnboardingCount} hidden`
                : null
            }
            onClick={() => scrollToSection("onboarding")}
          />
          <KpiTile
            label="Unhealthy"
            value={tiles?.unhealthy ?? "—"}
            numberClassName="text-danger-dark"
            hint={
              unhealthyAtRisk != null
                ? `${fmtMoney(unhealthyAtRisk)} of 90-day spend at risk`
                : null
            }
            onClick={() => scrollToSection("unhealthy")}
          />
          <KpiTile
            label="Monitoring"
            value={tiles?.monitoring ?? "—"}
            numberClassName="text-info-dark"
            onClick={() => scrollToSection("monitoring")}
          />
          <KpiTile
            label="Healthy"
            value={tiles?.healthy ?? "—"}
            numberClassName="text-success-dark"
            onClick={() => scrollToSection("healthy")}
          />
          {/* Between Healthy and Paused, matching the table's section order
              and the contract's field order: these hotels are still serving.
              Amber, not the gates' neutral — a hotel here is one the rules
              cannot judge, which is a standing caveat on the fleet's numbers
              rather than a settled fact like a pause. Counts both the daily
              pass's own reading of a Healthy hotel (backend ADR 0039) and the
              operator-placed category; the table's group header and each
              chip's hover say which. */}
          <KpiTile
            label="Low volume"
            value={tiles?.lowVolume ?? "—"}
            numberClassName="text-warning-dark"
            onClick={() => scrollToSection("low-volume")}
          />
          <KpiTile
            label="Paused"
            value={tiles?.paused ?? "—"}
            numberClassName="text-content-primary"
            onClick={() => scrollToSection("paused")}
          />
        </div>

        {/* Filter bar */}
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <div className="flex px-3 h-10 border border-neutral-400 rounded items-center gap-2.5 bg-white">
            <input
              className="text-sm outline-none placeholder:text-content-tertiary w-52"
              placeholder="Search hotel, site, email, customer ID…"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </div>
          <select
            className={SELECT_CLASS}
            value={engine}
            onChange={(e) => setParam({ engine: e.target.value })}
          >
            {engineOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          {/* Which window the metric columns lead with (census wtoggle). */}
          <span className="inline-flex rounded border border-neutral-400 overflow-hidden bg-white">
            {(
              [
                { key: "recent" as WindowMode, label: `Last ${metrics.data?.recentDays ?? 14}d` },
                { key: "full" as WindowMode, label: "90d" },
              ] as const
            ).map(({ key, label }) => (
              <button
                key={key}
                onClick={() => setParam({ w: key === "recent" ? "" : key })}
                className={clsx(
                  "px-3 h-[38px] text-xs font-semibold",
                  mode === key
                    ? "bg-brand-primary text-white"
                    : "text-content-secondary hover:text-content-primary",
                )}
              >
                {label}
              </button>
            ))}
          </span>
          <span className="ml-auto text-xs text-gray-400">
            {liveRows.length + onboardingRows.length} of{" "}
            {live.length + onboarding.length} hotels
          </span>
        </div>

        {hotels.isError ? (
          <LoadError
            error={hotels.error}
            label="the fleet"
            onRetry={() => hotels.refetch()}
          />
        ) : (
          <>
            {/* 1 — Still onboarding, ahead of the health states (the census
                separation: outside the states, the pipeline is the story).
                The section's anchor rides its group header row, inside the
                table, exactly as the health states' do. */}
            <div>
              {pipelines.isError && (
                <p className="mb-2 text-xs text-warning-dark">
                  Couldn't load the onboarding pipelines — showing
                  tracking-side rows only.
                </p>
              )}
              {/* Fail-open, and said out loud: a section that quietly showed
                  every row would read as "nobody has triaged any of these". */}
              {hiddenRows.isError && (
                <p className="mb-2 text-xs text-warning-dark">
                  Couldn't load the hidden-row list — every row is shown, and
                  hiding one may not stick.
                </p>
              )}
              {!hotels.isLoading && onboardingRows.length > 0 ? (
                <div className="mb-6">
                  <OnboardingSection
                    rows={onboardingRows}
                    hiddenRows={hiddenRows}
                    onOpenPipeline={(userId) => setDrawerUserId(userId)}
                    onOpenHotel={(customerId) =>
                      navigate(`/tracking/health/hotels/${customerId}`)
                    }
                  />
                </div>
              ) : !hotels.isLoading ? (
                <div className="bg-white rounded-lg shadow-sm border border-[#E9E9EB] px-4 py-3 mb-6 text-sm text-content-secondary">
                  No hotels onboarding right now.
                </div>
              ) : null}
            </div>

            {/* 2–6 — the flattened fleet, grouped Unhealthy → Monitoring →
                Healthy → Low volume → Paused inside ONE table (census
                staterows). */}
            <FleetTable
              rows={liveRows}
              metricsByCid={metricsByCid}
              metricsUnavailable={metrics.isError}
              mode={mode}
              recentDays={metrics.data?.recentDays ?? 14}
              isLoading={hotels.isLoading}
              error={null}
              retry={() => hotels.refetch()}
              onOpenHotel={(customerId) =>
                navigate(`/tracking/health/hotels/${customerId}`)
              }
              onLockAction={setLockModal}
              onEditNote={setNoteRow}
            />
          </>
        )}
      </div>

      {drawerRow?.user && (
        <UserDetailPanel
          user={drawerRow.user}
          trackingCustomerId={drawerRow.hotel?.customerId ?? null}
          onClose={() => setDrawerUserId(null)}
        />
      )}

      {/* Row-level lock action — same modal (and removal-consequence
          preview) as the hotel page, minus the navigation. */}
      {lockModal && (
        <LockActionModal
          customerId={lockModal.customerId}
          lock={lockModal.lock}
          action={lockModal.action}
          pending={applyLock.isPending || removeLock.isPending}
          onClose={() => setLockModal(null)}
          onSubmit={(reason) => {
            const vars = {
              customerId: lockModal.customerId,
              lock: lockModal.lock,
              reason,
            };
            const opts = { onSuccess: () => setLockModal(null) };
            if (lockModal.action === "apply") applyLock.mutate(vars, opts);
            else removeLock.mutate(vars, opts);
          }}
        />
      )}

      {/* The row's "Waiting on" tag writes the incident's blocked-on note —
          the same note, the same 7-day clock, reached without opening the
          hotel. Titled as a note on the hotel rather than as the question the
          column asks: an operator records whatever is true of this property
          right now, and "what is it waiting on?" turns away every note that
          is not a hand-off. */}
      {noteRow && (
        <NoteModal
          title={`Note — ${noteRow.hotelName}`}
          standing={standingNoteOf(noteRow)}
          pending={logNote.isPending}
          onClose={() => setNoteRow(null)}
          onSubmit={(note) =>
            logNote.mutate({ note }, { onSuccess: () => setNoteRow(null) })
          }
        />
      )}

      {/* The fleet-wide settings. The modal seeds its fields from the payload
          at mount, so it is not rendered until the read lands — the shell
          before that says so rather than opening on blanks that would read as
          "every setting is empty". */}
      {settingsOpen &&
        (settings.data ? (
          <SettingsModal
            payload={settings.data}
            pending={updateSettings.isPending}
            onClose={() => setSettingsOpen(false)}
            onSubmit={(body) =>
              updateSettings.mutate(body, {
                onSuccess: () => setSettingsOpen(false),
              })
            }
          />
        ) : (
          <ActionModal
            title="Tracking Health settings"
            submitLabel="Save changes"
            onSubmit={() => undefined}
            onClose={() => setSettingsOpen(false)}
            disabled
            wide
          >
            {settings.isError ? (
              <LoadError
                error={settings.error}
                label="the settings"
                onRetry={() => settings.refetch()}
              />
            ) : (
              <p className="text-sm text-content-secondary">Loading settings…</p>
            )}
          </ActionModal>
        ))}
      <TooltipLayer />
    </Page>
  );
}
